import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ProjectCover from './ui/ProjectCover';

/**
 * 3D rotating "image wheel" of projects. Auto-rotates, drag (mouse or touch)
 * to spin with inertia, click a card to open its case study.
 */
const Stage = styled.div`
  position: relative;
  height: 330px;
  margin-bottom: 3.5rem;
  perspective: 1500px;
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  cursor: grab;
  &:active { cursor: grabbing; }
  @media (max-width: 600px) { height: 270px; margin-bottom: 2.5rem; }
`;

const Wheel = styled.div`
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
  will-change: transform;
`;

const Panel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${(p) => p.$w}px;
  height: ${(p) => p.$h}px;
  margin: ${(p) => -p.$h / 2}px 0 0 ${(p) => -p.$w / 2}px;
  transform: ${(p) => `rotateY(${p.$angle}deg) translateZ(${p.$radius}px)`};
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid var(--border-strong);
  background: var(--bg-alt);
  box-shadow: var(--card-shadow);
  backface-visibility: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    display: block;
  }

  .cap {
    position: absolute;
    inset: auto 0 0 0;
    z-index: 2;
    padding: 1.8rem 1rem 0.8rem;
    background: linear-gradient(to top, rgba(5, 8, 18, 0.88), transparent);
    color: #fff;
    pointer-events: none;
    .t { font-family: var(--font-heading); font-weight: 700; font-size: 0.95rem; line-height: 1.25; }
    .o { font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase; opacity: 0.75; margin-top: 0.15rem; }
  }
`;

const Glow = styled.div`
  position: absolute;
  left: 50%;
  bottom: -40px;
  transform: translateX(-50%);
  width: 72%;
  height: 130px;
  background: radial-gradient(50% 100% at 50% 0%, var(--spotlight), transparent 70%);
  filter: blur(14px);
  pointer-events: none;
`;

const Hint = styled.div`
  text-align: center;
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-faint);
  margin-bottom: 1rem;
`;

const AUTO_SPEED = -0.05; // deg per frame
const DRAG_FACTOR = 0.25;

const panelSize = () => {
  const w = Math.min(330, Math.max(230, window.innerWidth * 0.62));
  return { w: Math.round(w), h: Math.round(w * 0.62) };
};

const ProjectWheel = ({ items, onOpen, hint }) => {
  const wheelRef = useRef(null);
  const angle = useRef(0);
  const velocity = useRef(0);
  const dragging = useRef(false);
  const hovering = useRef(false);
  const lastX = useRef(0);
  const moved = useRef(0);
  const [size, setSize] = useState(panelSize);

  useEffect(() => {
    const onResize = () => setSize(panelSize());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const n = items.length;
  const step = 360 / n;
  const radius = Math.round(size.w / 2 / Math.tan(Math.PI / n)) + 40;

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!dragging.current) {
        if (Math.abs(velocity.current) > 0.02) {
          angle.current += velocity.current;
          velocity.current *= 0.95;
        } else if (!hovering.current && !reduced) {
          angle.current += AUTO_SPEED;
        }
      }
      if (wheelRef.current) {
        wheelRef.current.style.transform =
          `translateZ(${-radius}px) rotateX(-5deg) rotateY(${angle.current}deg)`;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [radius]);

  const onPointerDown = (e) => {
    dragging.current = true;
    moved.current = 0;
    lastX.current = e.clientX;
    velocity.current = 0;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastX.current;
    lastX.current = e.clientX;
    angle.current += dx * DRAG_FACTOR;
    velocity.current = dx * DRAG_FACTOR;
    moved.current += Math.abs(dx);
  };
  const endDrag = () => { dragging.current = false; };

  return (
    <>
      <Hint>{hint || 'Drag to spin — click a card to open'}</Hint>
      <Stage
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
        onMouseEnter={() => { hovering.current = true; }}
        onMouseLeave={() => { hovering.current = false; }}
      >
        <Glow />
        <Wheel ref={wheelRef}>
          {items.map((p, i) => (
            <Panel
              key={p.id}
              $w={size.w}
              $h={size.h}
              $angle={i * step}
              $radius={radius}
              onClick={() => { if (moved.current < 8) onOpen(p.id); }}
            >
              {p.image
                ? <img src={p.image} alt={p.title} loading="lazy" draggable={false} />
                : <ProjectCover cover={p.cover} org={p.org} />}
              <div className="cap">
                <div className="t">{p.title}</div>
                <div className="o">{p.org}</div>
              </div>
            </Panel>
          ))}
        </Wheel>
      </Stage>
    </>
  );
};

export default ProjectWheel;
